import { useCallback, useEffect, useState } from "react";
import { RefreshCw, Send, CheckCircle2, Clock, AlertTriangle, History } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";

const STATUS_STYLE = {
  delivered: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  pending: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  failed: "bg-red-500/15 text-red-300 border-red-500/30",
  uncertain: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  skipped: "bg-white/10 text-white/60 border-white/20",
};

const statusLabel = (it) => {
  if (it.status === "delivered") return it.reconciled ? "delivered (reconciled)" : "delivered";
  if (it.status === "uncertain") return "uncertain — needs your decision";
  if (it.status === "skipped") return "skipped — no contact";
  return it.status;
};

const fmtIST = (iso) => {
  if (!iso) return "—";
  const p = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true,
    }).formatToParts(new Date(iso)).map((x) => [x.type, x.value])
  );
  return `${p.day} ${p.month} ${p.year}, ${p.hour}:${p.minute} ${p.dayPeriod}`;
};

export const CrmDeliveries = () => {
  const [data, setData] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/crm-deliveries");
      setData(data);
    } catch {
      toast.error("Could not load CRM deliveries");
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const retry = async (id) => {
    setBusy(id);
    try {
      const { data: r } = await api.post(`/admin/crm-deliveries/${id}/retry`);
      r.ok ? toast.success("Delivered to CRM") : toast.error(r.error || "Retry failed");
      await load();
    } finally { setBusy(null); }
  };

  const previewBackfill = async () => {
    const { data } = await api.get("/admin/crm-deliveries/backfill/preview");
    setPreview(data);
  };

  const runBackfill = async () => {
    setBusy("backfill");
    try {
      const { data } = await api.post("/admin/crm-deliveries/backfill");
      toast.success(`${data.queued_total} past enquiries queued`);
      setPreview(null);
      await load();
    } finally { setBusy(null); }
  };

  const runReconcile = async () => {
    if (!window.confirm("Reconcile all pending items against the live CRM and send only the genuinely new ones? Only do this after the fictional test enquiry has been confirmed in the CRM.")) return;
    setBusy("reconcile");
    try {
      const { data: r } = await api.post("/admin/crm-deliveries/reconcile");
      toast.success(`Reconciled ${r.reconciled} · ${r.queued_new} new queued · ${r.uncertain} uncertain · ${r.skipped_no_contact} skipped`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconciliation failed");
    } finally { setBusy(null); }
  };

  const cfg = data?.config;
  const counts = data?.counts || {};
  const run = data?.backlog_run;

  return (
    <div data-testid="crm-deliveries-panel">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <span className="vp-label text-white/60 mb-2 block">SYNC</span>
          <h1 className="font-serif-vp text-4xl md:text-5xl">CRM deliveries</h1>
          <p className="text-white/60 mt-2 text-sm break-all" data-testid="crm-config-status">
            {!cfg ? "Loading…"
              : !cfg.configured ? "CRM address / secret not configured yet — enquiries are queued and will be sent once configured."
              : !cfg.enabled ? `Configured (${cfg.url}) · forwarding is OFF — manual Retry still delivers.`
              : `Forwarding ON → ${cfg.url}`}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Stat icon={Clock} label="Pending" value={counts.pending || 0} testId="crm-count-pending" />
          <Stat icon={CheckCircle2} label="Delivered" value={counts.delivered || 0} testId="crm-count-delivered" />
          <Stat icon={AlertTriangle} label="Failed" value={counts.failed || 0} testId="crm-count-failed" />
          {counts.uncertain > 0 && <Stat icon={AlertTriangle} label="Uncertain" value={counts.uncertain} testId="crm-count-uncertain" />}
          <button onClick={load} className="flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-white/70 hover:text-white" data-testid="crm-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      <div className="border border-white/10 rounded-lg p-5 mb-8 flex items-center justify-between flex-wrap gap-4" data-testid="crm-backfill-box">
        <div className="text-sm text-white/70 flex items-start gap-3">
          <History className="w-4 h-4 mt-0.5 text-white/50" />
          <div>
            <div className="text-white">Import past website enquiries into the CRM</div>
            {preview ? (
              <div className="mt-1 text-white/60" data-testid="crm-backfill-preview">
                {preview.total_leads} leads on the website · {preview.importable} to queue · {preview.already_queued} already queued ·{" "}
                {preview.no_contact_skipped} skipped (no phone/email)
                {preview.oldest && <> · {fmtIST(preview.oldest)} → {fmtIST(preview.newest)}</>}
              </div>
            ) : (
              <div className="mt-1 text-white/50">Preview first; nothing is sent until you confirm. Every distinct enquiry is kept — the CRM groups repeats under the same customer.</div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={previewBackfill} className="px-4 py-2 text-xs tracking-[0.18em] uppercase border border-white/20 rounded-md hover:bg-white/5" data-testid="crm-backfill-preview-btn">Preview</button>
          <button onClick={runBackfill} disabled={!preview || preview.importable === 0 || busy === "backfill"} className="px-4 py-2 text-xs tracking-[0.18em] uppercase bg-[var(--vp-accent)] text-black rounded-md disabled:opacity-40" data-testid="crm-backfill-run-btn">
            Queue {preview?.importable ?? ""} for import
          </button>
        </div>
      </div>

      {(counts.pending > 0 || run) && (
        <div className="border border-white/10 rounded-lg p-5 mb-8 flex items-center justify-between flex-wrap gap-4" data-testid="crm-reconcile-box">
          <div className="text-sm text-white/70 flex items-start gap-3">
            <Send className="w-4 h-4 mt-0.5 text-white/50" />
            <div>
              <div className="text-white">Reconcile pending items with the live CRM, then send only the new ones</div>
              <div className="mt-1 text-white/60" data-testid="crm-reconcile-summary">
                {run ? (
                  <>
                    {run.status === "running" ? "Running" : `Completed ${fmtIST(run.finished_at)}`} · {run.pending_before} pending before · {run.reconciled} already in CRM (reconciled)
                    {run.summary ? <> · {run.summary.sent} sent · {run.summary.needs_review} in CRM Lead inbox · {run.summary.still_pending_or_failed} still pending/failed</> : <> · {run.queued_new} new being sent</>}
                    {" "}· {run.uncertain} uncertain · {run.skipped_no_contact} skipped (no contact)
                    {run.summary_email && <> · summary email {run.summary_email.status}</>}
                  </>
                ) : (
                  "Items already imported earlier are confirmed with the CRM by their event ID and marked reconciled; look-alikes are left for your decision; no per-enquiry emails, one summary email at the end."
                )}
              </div>
            </div>
          </div>
          <button onClick={runReconcile} disabled={!cfg?.enabled || busy === "reconcile" || !(counts.pending > 0)} className="px-4 py-2 text-xs tracking-[0.18em] uppercase bg-[var(--vp-accent)] text-black rounded-md disabled:opacity-40" data-testid="crm-reconcile-run-btn">
            Reconcile & send backlog
          </button>
        </div>
      )}

      <div className="border border-white/10 rounded-lg overflow-x-auto" data-testid="crm-deliveries-table">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-white/5 text-xs uppercase tracking-wider text-white/60">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Enquiry</th>
              <th className="text-left px-4 py-3 font-medium">Source</th>
              <th className="text-left px-4 py-3 font-medium">Received</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-left px-4 py-3 font-medium">Attempts</th>
              <th className="text-left px-4 py-3 font-medium">Last error / CRM ref</th>
              <th className="text-left px-4 py-3 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {!data ? (
              <tr><td colSpan={7} className="text-center py-10 text-white/50">Loading…</td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-16 text-white/50">No deliveries yet — new enquiries are queued automatically.</td></tr>
            ) : data.items.map((it) => (
              <tr key={it.id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`crm-row-${it.id}`}>
                <td className="px-4 py-4">
                  <div className="font-medium">{it.lead_name || "—"}</div>
                  <div className="text-white/50 text-xs">{it.lead_phone} · {it.destination || "—"}</div>
                </td>
                <td className="px-4 py-4 text-white/80">{it.source}</td>
                <td className="px-4 py-4 text-white/60 whitespace-nowrap">{fmtIST(it.lead_created_at)}</td>
                <td className="px-4 py-4">
                  <Badge className={`border ${STATUS_STYLE[it.status] || ""} hover:bg-transparent`} data-testid={`crm-status-${it.id}`}>{statusLabel(it)}</Badge>
                </td>
                <td className="px-4 py-4 text-white/60">{it.attempts}</td>
                <td className="px-4 py-4 text-white/60 max-w-sm truncate" title={it.last_error || ""}>
                  {it.status === "delivered"
                    ? `${it.crm?.results?.[0]?.status === "needs_review" ? "In CRM Lead inbox (needs review)" : it.reconciled ? "Already in CRM — confirmed by event ID" : "CRM enquiry created"} · ${fmtIST(it.delivered_at)}${it.backfill ? " · history" : it.alert ? ` · email ${it.alert.status}` : ""}`
                    : (it.last_error || "Waiting for sender")}
                </td>
                <td className="px-4 py-4 text-right">
                  {it.status !== "delivered" && (
                    <button onClick={() => retry(it.id)} disabled={busy === it.id} className="inline-flex items-center gap-1.5 text-xs tracking-[0.18em] uppercase text-[var(--vp-accent)] hover:text-white disabled:opacity-40" data-testid={`crm-retry-${it.id}`}>
                      <Send className="w-3.5 h-3.5" /> Retry
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-center gap-2 px-3 py-2 border border-white/10 rounded-md text-xs" data-testid={testId}>
    <Icon className="w-3.5 h-3.5 text-white/50" />
    <span className="text-white/60 uppercase tracking-wider">{label}</span>
    <span className="text-white font-medium">{value}</span>
  </div>
);
